import { useState } from 'react'
import { addPayRate, loadPayHistory, type PayRate } from '@/data/portal'
import { useAsync } from '@/data/useAsync'
import { Failed, Loading } from '@/components/bits'
import { shortDate, today } from '@/lib/format'
import type { PersonDetail } from '@/data/queries'
import { useOrg } from '@/org/OrgProvider'

const REASONS = [
  'Annual pay review',
  'Appraisal outcome',
  'Promotion',
  'Change of role',
  'National Living Wage uprating',
  'Correction to a previous rate',
  'Other',
]

function money(value: number | null, currency: string): string {
  if (value === null) return '—'
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(value)
}

function amountOf(r: PayRate): number | null {
  return r.hourlyRate ?? r.annualSalary
}

function Row({ rate, previous, isCurrent }: { rate: PayRate; previous: PayRate | null; isCurrent: boolean }) {
  const amount = amountOf(rate)
  const before = previous === null ? null : amountOf(previous)
  const change = amount !== null && before !== null && before !== 0 ? ((amount - before) / before) * 100 : null

  return (
    <div className={isCurrent ? 'tl current' : 'tl'}>
      <div className="when">{shortDate(rate.effectiveFrom)}</div>
      <div className="what">
        <div className="headline">
          <span className="figure">{money(amount, rate.currency)}</span>
          <span style={{ fontSize: 13, color: 'var(--ivory-dim)' }}>
            {rate.hourlyRate !== null ? 'per hour' : 'per year'}
          </span>
          {change !== null && (
            <span className={change >= 0 ? 'delta up' : 'delta down'}>
              {change >= 0 ? '+' : ''}
              {change.toFixed(1)}%
            </span>
          )}
          {isCurrent && <span className="sev due_soon">In force</span>}
        </div>
        <p>
          {rate.reason ??
            'No reason recorded. For a sponsored worker a pay change is reportable to the Home Office, and the report has to state why.'}
        </p>
        <div className="meta">
          {rate.effectiveTo === null
            ? 'Open ended'
            : `Until ${shortDate(rate.effectiveTo)}`}
          {rate.source !== 'manual' && ` · from ${rate.source}`}
        </div>
      </div>
    </div>
  )
}

export function Pay({ person }: { person: PersonDetail }) {
  const { current } = useOrg()
  const result = useAsync(
    () => loadPayHistory(current.id, person.employment.id),
    [current.id, person.employment.id],
  )

  const [open, setOpen] = useState(false)
  const [basis, setBasis] = useState<'hourly' | 'annual'>('hourly')
  const [amount, setAmount] = useState('')
  const [from, setFrom] = useState(today())
  const [reason, setReason] = useState(REASONS[0]!)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (result.state === 'loading') return <Loading what="pay history" />
  if (result.state === 'error') return <Failed message={result.message} onRetry={result.reload} />

  const rates = result.data
  const now = today()
  // In force means started and not yet ended. There should be exactly one.
  const currentIndex = rates.findIndex(
    (r) => r.effectiveFrom <= now && (r.effectiveTo === null || r.effectiveTo >= now),
  )
  const inForce = currentIndex === -1 ? null : rates[currentIndex]!
  const parsed = Number(amount)
  const valid = amount.trim() !== '' && Number.isFinite(parsed) && parsed > 0

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await addPayRate({
        organisationId: current.id,
        employmentId: person.employment.id,
        effectiveFrom: from,
        hourlyRate: basis === 'hourly' ? parsed : null,
        annualSalary: basis === 'annual' ? parsed : null,
        reason,
        supersedesId: inForce?.id ?? null,
      })
      setOpen(false)
      setAmount('')
      result.reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="head">
        <h2>Pay</h2>
        <div className="spacer" />
        <span className="owner">We own this</span>
        <button className="btn sm" type="button" onClick={() => setOpen((v) => !v)}>
          {open ? 'Cancel' : 'Record a change'}
        </button>
      </div>

      {open && (
        <div className="addform">
          <h3>New rate</h3>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ivory-dim)', maxWidth: '66ch' }}>
            The rate in force is closed off the day before this one starts, so there is never more
            than one answer to what somebody is paid today. Pay is not sent to Planday: it stays
            here.
          </p>
          <div className="grid">
            <label>
              Basis
              <select value={basis} onChange={(e) => setBasis(e.target.value as 'hourly' | 'annual')}>
                <option value="hourly">Hourly rate</option>
                <option value="annual">Annual salary</option>
              </select>
            </label>
            <label>
              Amount
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={basis === 'hourly' ? '12.60' : '32000'}
              />
            </label>
            <label>
              Effective from
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label>
              Reason
              <select value={reason} onChange={(e) => setReason(e.target.value)}>
                {REASONS.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </label>
            <div>
              <button
                className="btn"
                type="button"
                onClick={() => void save()}
                disabled={busy || !valid}
              >
                {busy ? 'Saving…' : 'Save rate'}
              </button>
            </div>
          </div>
          {error !== null && (
            <div className="callout" style={{ marginTop: 14, borderLeftColor: 'var(--sev-critical)' }}>
              {error}
            </div>
          )}
        </div>
      )}

      <div className="body flush">
        {rates.length === 0 ? (
          <div className="empty">
            <p>No pay recorded. Nothing was imported from Planday, because pay is ours to hold.</p>
          </div>
        ) : (
          <div className="timeline">
            {rates.map((r, i) => (
              <Row
                key={r.id}
                rate={r}
                previous={rates[i + 1] ?? null}
                isCurrent={i === currentIndex}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
